"use client";

import { FEEDBACK_TYPE_CONFIG, FeedbackType } from "./types";
import { cn } from "@/lib/utils";

interface FeedbackTypeSelectorProps {
  value: FeedbackType;
  onChange: (type: FeedbackType) => void;
}

/**
 * A toggle-style button group for selecting feedback type.
 *
 * ## Why we use custom buttons instead of the Button component
 *
 * The existing Button component doesn't have a suitable variant for this use case:
 *
 * 1. **Toggle/Selected State**: The Button component lacks a "selected" state that
 *    changes the background and border colors to indicate active selection.
 *
 * 2. **Different Visual Pattern**: This is a toggle group pattern where:
 *    - Buttons need equal width (flex-1)
 *    - Selected state uses primary color with transparency (bg-cede-primary/20)
 *    - Unselected state has a subtle hover effect on the border
 *    - The pattern is more like radio buttons than action buttons
 *
 * 3. **Styling Constraints**: The Button component uses predefined variants with
 *    specific padding, heights, and border-radius that don't match this compact
 *    toggle design.
 *
 * If this pattern is needed in multiple places, consider creating a `ToggleGroup`
 * or `SegmentedControl` component in the UI library.
 */
export function FeedbackTypeSelector({ value, onChange }: FeedbackTypeSelectorProps) {
  const allTypes = Object.keys(FEEDBACK_TYPE_CONFIG) as FeedbackType[];
  const firstRowTypes = allTypes.slice(0, 3); // Bug, Feature, Feedback
  const secondRowTypes = allTypes.slice(3); // PnL, Liquidity issues

  const renderButton = (type: FeedbackType) => {
    const config = FEEDBACK_TYPE_CONFIG[type];
    const isSelected = value === type;
    return (
      <button
        key={type}
        type="button"
        onClick={() => onChange(type)}
        className={cn(
          "flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all duration-200",
          "border",
          isSelected
            ? "bg-cede-primary/20 border-cede-primary text-cede-primary"
            : "bg-transparent border-cede-border-container text-cede-text-secondary hover:border-cede-text-secondary hover:text-cede-text-primary",
        )}
      >
        <span className="mr-1">{config.emoji}</span>
        {config.label}
      </button>
    );
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-2">{firstRowTypes.map(renderButton)}</div>
      {secondRowTypes.length > 0 && <div className="flex gap-2">{secondRowTypes.map(renderButton)}</div>}
    </div>
  );
}
